import { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { MenuIcon } from 'lucide-react';
import { useSidebar } from '@worldcoo/ui/components/sidebar';

export function MainSection(props: { children: React.ReactNode }) {
	return (
		<div className="flex min-h-0 min-w-0 flex-1 p-2 md:p-3">
			<main
				className="border-portal-border flex h-full max-w-[1280px] min-w-0 flex-1 flex-col overflow-hidden rounded-[16px] border bg-white"
				style={{ viewTransitionName: 'ngo-main-content' }}
			>
				{props.children}
			</main>
		</div>
	);
}

function MobileMenuButton() {
	const { isMobile, setOpenMobile } = useSidebar();
	if (!isMobile) return null;
	return (
		<button
			type="button"
			onClick={() => setOpenMobile(true)}
			aria-label="Open menu"
			className="mr-1 -ml-2 inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-[#1d1d1b] hover:bg-[#fff7ed]"
		>
			<MenuIcon className="size-5" />
		</button>
	);
}

export function MainSectionHeader(props: {
	breadcrumbs?: React.ReactNode;
	title: React.ReactNode;
	actions?: React.ReactNode;
	className?: string;
}) {
	const prevHasBreadcrumbsRef = useRef<boolean | null>(null);
	const hasBreadcrumbs = !!props.breadcrumbs;
	const shouldAnimate = prevHasBreadcrumbsRef.current !== null && prevHasBreadcrumbsRef.current !== hasBreadcrumbs;

	useEffect(() => {
		prevHasBreadcrumbsRef.current = hasBreadcrumbs;
	}, [hasBreadcrumbs]);

	return (
		<div
			className={`border-portal-border flex min-h-20 shrink-0 flex-wrap items-center gap-2 border-b bg-white px-4 py-3 md:min-h-24 md:flex-nowrap md:px-6 md:py-4 ${props.className ?? ''}`}
		>
			<MobileMenuButton />
			<motion.div
				key={hasBreadcrumbs ? 'detail' : 'list'}
				initial={shouldAnimate ? { y: hasBreadcrumbs ? -12 : 12 } : false}
				animate={{ y: 0 }}
				transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
				className="flex min-w-0 flex-col gap-1"
			>
				{props.breadcrumbs && (
					<motion.div
						initial={shouldAnimate ? { opacity: 0 } : false}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
					>
						{props.breadcrumbs}
					</motion.div>
				)}
				{props.title}
			</motion.div>
			{props.actions && (
				<div className="flex flex-1 basis-full flex-wrap items-center justify-end gap-2 md:basis-auto">
					{props.actions}
				</div>
			)}
		</div>
	);
}

export function MainSectionContent(props: {
	children: React.ReactNode;
	className?: string;
	ref?: React.Ref<HTMLDivElement>;
}) {
	return (
		<div ref={props.ref} className={`flex flex-1 flex-col overflow-y-auto ${props.className ?? ''}`}>
			{props.children}
		</div>
	);
}

export function MainSectionFooter(props: { children: React.ReactNode; className?: string }) {
	return (
		<div
			className={`border-portal-border shrink-0 border-t bg-white px-4 py-3 md:px-6 md:py-4 ${props.className ?? ''}`}
		>
			{props.children}
		</div>
	);
}
